// @cogo/webmcp-runtime — WebMCP runtime for Cogo apps

// Core
export {
  CogoToolRegistry,
  type WebMCPTool,
  type WebMCPToolMetadata,
  type WebMCPToolInvocationContext,
  type JsonSchema,
  type ToolManifestEntry,
  type ToolsChangedListener,
} from "./core/tool-registry"

export {
  validate,
  validateInput,
  validateOutput,
  type ValidationResult,
  type ValidationError,
} from "./core/schema-validator"

export {
  WebMCPStateProvider,
  type StateManagerLike,
} from "./core/state-provider"

// Adapters
export {
  installRuntimePolyfill,
  uninstallRuntimePolyfill,
  installDebugHook,
  notifyFlowStatus,
  type CogoToolsGlobal,
  type CogoRuntimeRef,
  type CogoDebugAPI,
} from "./adapters/runtime-adapter"

export {
  installBrowserAdapter,
} from "./adapters/browser-adapter"

// Renderer integration
export {
  WebMCPProvider,
  WebMCPContext,
  useWebMCPContext,
  type WebMCPContextValue,
  type WebMCPProviderProps,
  type WebMCPOntologyManifest,
} from "./renderer/webmcp-provider"

export {
  useWebMCPRegistry,
  useWebMCPState,
  useWebMCPTool,
} from "./renderer/use-webmcp"

export {
  createWebMCPIntegration,
  type WebMCPIntegration,
  type WebMCPOptions,
  type CogoRuntimeLike,
  type CogoAppDefLike,
  type CogoBlockDefLike,
} from "./renderer/renderer-plugin"

export {
  useCogoWebMCP,
  type UseCogoWebMCPResult,
} from "./renderer/use-cogo-webmcp"

// Generators (DSL → WebMCP Tools)
export { generateFormTools, type FormField, type GeneratorRuntime, type ActionEngineLike } from "./generators/form-tools"
export { generateActionFlowTools } from "./generators/action-flow-tools"
export { generateDataTableTools } from "./generators/data-table-tools"
export { generateNavigationTools } from "./generators/navigation-tools"
export { generateCrudTools } from "./generators/crud-tools"
export { generateCartTools } from "./generators/cart-tools"
export {
  generatePageContextTools,
  generateVisibleElementsTools,
  type PageContextRuntime,
} from "./generators/page-context-tools"
export {
  generateToolsFromBlock,
  generateToolsFromPage,
  generateToolsFromApp,
} from "./generators/dsl-to-tools"

// Agent — Gemini native function calling + persona system
export {
  GeminiAgentRuntime,
  GeminiLlmDriver,
  DgxLlmDriver,
  toOpenAiMessages,
  DGX_DEFAULT_MAX_TOKENS,
  DGX_TIMEOUT_MS,
  type DgxLlmDriverOptions,
  type LlmDriver,
  type LlmGenerateArgs,
  type LlmGenerateResult,
  type LlmToolDecl,
  type NormalizedContent,
  type NormalizedPart,
  type AgentContentPart,
  type AgentRunResult,
  type AgentToolCallOptions,
  type AgentToolExecutor,
  requiresConfirmation,
  buildConfirmation,
  type ConfirmationResult,
  formatToolError,
  classifyError,
  type ErrorClassification,
  type AgentPersona,
  shoppingPersona,
  dashboardPersona,
  genericPersona,
  partnerPersona,
  detectPersona,
} from "./agent"

// Ontology — standalone AppOntology extraction (no IDE dependencies)
export {
  extractAppOntology,
  buildCrossRefFromBlocks,
  ontologyToManifest,
  buildOntologySystemPrompt,
  buildOntologySummary,
  enrichAgentWithOntology,
  type AppOntology,
  type OntologyDatasource,
  type OntologyFlowNode,
  type OntologyStateNode,
  type OntologyUIBinding,
  type OntologyEdge,
  type OntologyNode,
  type BlockInput,
  type MinimalCrossRef,
  type OntologySummary,
  type OntologyPromptOptions,
} from "./ontology"

// Orchestration — typed plan and durable execution-event wire contracts
export {
  COOGO_ORCHESTRATION_PLAN_V1_VERSION,
  COOGO_ORCHESTRATION_PLAN_V2_VERSION,
  COOGO_ORCHESTRATION_PLAN_VERSION,
  COOGO_EXECUTION_EVENT_V1_VERSION,
  COOGO_EXECUTION_EVENT_V2_VERSION,
  COOGO_EXECUTION_EVENT_VERSION,
  parseCoogoDependency,
  normalizedDependencies,
  validateCoogoOrchestrationPlan,
  validateCoogoExecutionEvent,
  type CoogoOrchestrationPlanVersion,
  type CoogoExecutionEventVersion,
  type CoogoOrchestrationTrigger,
  type CoogoOrchestrationNodeKind,
  type CoogoOrchestrationDependencyCondition,
  type CoogoConditionalDependency,
  type CoogoOrchestrationDependency,
  type NormalizedCoogoDependency,
  type CoogoHumanGateAdmission,
  type CoogoOrchestrationNodePolicy,
  type CoogoRagEvidenceScope,
  type CoogoOrchestrationNode,
  type CoogoOrchestrationNodeV1,
  type CoogoOrchestrationNodeV2,
  type CoogoOrchestrationPlan,
  type CoogoOrchestrationPlanV1,
  type CoogoOrchestrationPlanV2,
  type CoogoReadableOrchestrationPlan,
  type CoogoExecutionEventType,
  type CoogoExecutionEventTypeV1,
  type CoogoExecutionEventTypeV2,
  type CoogoExecutionEvent,
  type CoogoExecutionEventV1,
  type CoogoExecutionEventV2,
  type CoogoReadableExecutionEvent,
  type CoogoContractValidationResult,
  COOGO_EXECUTION_SNAPSHOT_VERSION,
  COOGO_EXECUTION_CHECKPOINT_V1_VERSION,
  COOGO_MAX_LOOP_ITERATIONS,
  CoogoExecutionError,
  CoogoExecutionTimeoutError,
  CoogoExecutionCancelledError,
  CoogoOrchestrationValidationError,
  LegacyInMemoryCoogoExecutionSnapshotJournal,
  CoogoOrchestrationHandlerRegistry,
  CoogoOrchestrationExecutor,
  createCoogoMockHandler,
  createCoogoLoopHandler,
  createCoogoSubplanHandler,
  parseCoogoExecutionCheckpointV1,
  validateCoogoExecutionTransition,
  type CoogoNodeExecutionStatus,
  type CoogoPlanExecutionStatus,
  type CoogoHandlerTelemetry,
  type CoogoNodeTelemetry,
  type CoogoSuspension,
  type CoogoNodeHandlerResult,
  type CoogoNodeExecutionRecord,
  type CoogoSerializedExecutionError,
  type CoogoExecutionSnapshot,
  type CoogoCheckpointJsonPrimitive,
  type CoogoCheckpointObservationV1,
  type CoogoCheckpointExactImpactOutputV1,
  type CoogoCheckpointCompensationOutputV1,
  type CoogoCheckpointSafeOutputV1,
  type CoogoCheckpointGateSuspensionV1,
  type CoogoCheckpointAdmissionBindingV1,
  type CoogoExecutionCheckpointNodeV1,
  type CoogoExecutionCheckpointV1,
  type CoogoLoadedExecution,
  type CoogoExecutionTransition,
  type CoogoExecutionTransitionReceipt,
  type CoogoExecutionJournal,
  type LegacyCoogoExecutionSnapshotJournal,
  type CoogoExecutionLease,
  type CoogoExecutionLeaseController,
  type CoogoSubplanExecutionOptions,
  type CoogoNodeHandlerContext,
  type CoogoNodeHandler,
  type CoogoHandlerMap,
  type CoogoMockDefinition,
  type CoogoExecutorDependencies,
  type CoogoExecuteOptions,
  type CoogoExecutionResult,
  type CoogoLoopBodyContext,
  type CoogoLoopBodyResult,
  type CoogoLoopHandlerOptions,
  type CoogoSubplanResolver,
} from "./orchestration"

// Testing — Node.js only (uses node:fs). Import from "@cogo/webmcp-runtime/testing" in Node contexts.
// DO NOT export here — breaks browser bundles (Vite externalizes node:fs).
// See: packages/webmcp-runtime/src/testing/index.ts
